import {
  APPLY_CHUNK_SIZE,
  contactSlugBackfillService,
  type ApplyRowResult,
} from "../../../api/contactSlugBackfill.service.js";

/**
 * Everything a chunked run produced: the per-contact results that came back,
 * and the failure that ended it early — or null for a run that sent every
 * chunk. A partial run's results are the only record of what was written, so
 * they are returned alongside the failure rather than being thrown away with
 * it.
 */
export type ChunkedApplyOutcome = {
  results: ApplyRowResult[];
  failure: string | null;
};

/**
 * Runs the contact ids through the apply endpoint in chunks of
 * APPLY_CHUNK_SIZE, sequentially, banking every chunk that comes back.
 *
 * Deliberately resolves rather than throws: a chunk that fails stops the run,
 * and the caller still gets everything the earlier chunks returned.
 */
export async function runChunkedApply(
  contactIds: string[],
  onProgress: (done: number, total: number) => void,
): Promise<ChunkedApplyOutcome> {
  const results: ApplyRowResult[] = [];
  onProgress(0, contactIds.length);

  for (let start = 0; start < contactIds.length; start += APPLY_CHUNK_SIZE) {
    const chunk = contactIds.slice(start, start + APPLY_CHUNK_SIZE);
    try {
      const response = await contactSlugBackfillService.apply(chunk);
      results.push(...response.results);
    } catch (applyError) {
      const message = applyError instanceof Error ? applyError.message : String(applyError);
      // A request that failed may still have written rows: a gateway timeout
      // cuts the connection, not the transaction the API is part-way through.
      return {
        results,
        failure:
          `Stopped after ${results.length.toLocaleString()} of ` +
          `${contactIds.length.toLocaleString()} contacts: ${message} ` +
          "Contacts in the failed batch may or may not have been written — " +
          "re-scan before retrying.",
      };
    }
    onProgress(Math.min(start + chunk.length, contactIds.length), contactIds.length);
  }

  return { results, failure: null };
}
